import { useEffect, useState } from 'react';
import type { ColumnDef } from '@tanstack/react-table';
import { trialOTPService } from '../../services/adminService';
import { DataTable } from '../../components/ui/data-table';
import { toast } from 'react-hot-toast';

export function TrialOTPs() {
  const [otps, setOTPs] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [creating, setCreating] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [selectedOTP, setSelectedOTP] = useState<any>(null);
  const [newOTPCode, setNewOTPCode] = useState('');
  const [trialDays, setTrialDays] = useState<number>(7);
  const [description, setDescription] = useState('');

  const load = async () => {
    try {
      setIsLoading(true);
      const result = await trialOTPService.listOTPs(1, 50);
      setOTPs(result.items);
    } catch (e: any) {
      toast.error('Failed to load OTPs');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const create = async () => {
    try {
      setCreating(true);
      const res = await trialOTPService.createOTP(trialDays, description || undefined);
      await load();
      setNewOTPCode(res.code);
      setShowCreateModal(true);
      setDescription('');
      try {
        await navigator.clipboard.writeText(res.code);
        toast.success('OTP created and copied to clipboard');
      } catch {
        toast.success('OTP created');
      }
    } catch (e: any) {
      toast.error(e.response?.data?.message || 'Failed to create OTP');
    } finally {
      setCreating(false);
    }
  };

  const viewDetails = async (otpId: string) => {
    try {
      const details = await trialOTPService.getOTPDetails(otpId);
      setSelectedOTP(details);
      setShowDetailsModal(true);
    } catch (e: any) {
      toast.error('Failed to load OTP details');
    }
  };

  const toggleStatus = async (otpId: string, currentStatus: boolean) => {
    try {
      await trialOTPService.toggleOTPStatus(otpId, !currentStatus);
      toast.success(`OTP ${currentStatus ? 'deactivated' : 'activated'}`);
      await load();
    } catch (e: any) {
      toast.error('Failed to update OTP status');
    }
  };

  const totalUsages = otps.reduce((sum, otp) => sum + otp.usageCount, 0);
  const activeOTPs = otps.filter((otp) => otp.isActive).length;

  const columns: ColumnDef<any>[] = [
    {
      id: 'code',
      header: 'OTP Code',
      cell: ({ row }) => (
        <span className="font-mono font-semibold text-base">{row.original.code}</span>
      ),
      meta: { width: 'w-36' },
    },
    {
      id: 'description',
      header: 'Description',
      cell: ({ row }) => (
        <span className="text-muted-foreground">{row.original.description || '-'}</span>
      ),
    },
    {
      id: 'trialDays',
      header: 'Trial Days',
      cell: ({ row }) => <span>{row.original.trialDays} days</span>,
      meta: { width: 'w-28' },
    },
    {
      id: 'activations',
      header: 'Activations',
      cell: ({ row }) => (
        <button
          onClick={() => viewDetails(row.original.id)}
          className="text-blue-600 dark:text-blue-400 hover:underline"
        >
          {row.original.usageCount} brands
        </button>
      ),
      meta: { width: 'w-32' },
    },
    {
      id: 'status',
      header: 'Status',
      cell: ({ row }) => (
        <span
          className={`inline-flex items-center px-2 py-1 rounded text-xs ${
            row.original.isActive
              ? 'bg-green-100 dark:bg-green-500/15 text-green-700 dark:text-green-300'
              : 'bg-muted text-muted-foreground'
          }`}
        >
          {row.original.isActive ? 'Active' : 'Inactive'}
        </span>
      ),
      meta: { width: 'w-24' },
    },
    {
      id: 'createdAt',
      header: 'Created',
      cell: ({ row }) => (
        <span>{new Date(row.original.createdAt).toLocaleDateString()}</span>
      ),
      meta: { width: 'w-28' },
    },
    {
      id: 'actions',
      header: 'Actions',
      cell: ({ row }) => (
        <div className="space-x-2 whitespace-nowrap">
          <button
            onClick={async () => {
              await navigator.clipboard.writeText(row.original.code);
              toast.success('Copied OTP code');
            }}
            className="px-3 py-1.5 border border-border rounded-lg hover:bg-accent text-xs"
          >
            Copy Code
          </button>
          <button
            onClick={() => toggleStatus(row.original.id, row.original.isActive)}
            className={`px-3 py-1.5 rounded-lg text-xs ${
              row.original.isActive
                ? 'border border-border hover:bg-accent'
                : 'bg-green-600 text-white hover:bg-green-700'
            }`}
          >
            {row.original.isActive ? 'Deactivate' : 'Activate'}
          </button>
        </div>
      ),
      meta: { width: 'w-48' },
    },
  ];

  return (
    <div className="space-y-6">
      {/* Header & Action */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-foreground">Trial OTP Codes</h1>
          <p className="text-sm text-muted-foreground">Create reusable OTP codes for brand trial activation.</p>
        </div>
        <button
          onClick={create}
          disabled={creating}
          className="inline-flex items-center px-4 py-2 rounded-lg bg-[#d7785b] text-white hover:bg-[#c86c51] transition disabled:opacity-50"
        >
          {creating ? (
            <>
              <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
              Creating...
            </>
          ) : (
            'Create OTP Code'
          )}
        </button>
      </div>

      {/* Create OTP Form */}
      <div className="bg-card border border-border rounded-xl p-4">
        <div className="flex flex-wrap items-end gap-4">
          <div className="flex-1 min-w-[200px]">
            <label className="block text-sm font-medium text-muted-foreground mb-1">Trial Days</label>
            <input
              type="number"
              min="1"
              max="30"
              value={trialDays}
              onChange={(e) => {
                const value = parseInt(e.target.value, 10);
                if (!isNaN(value) && value >= 1 && value <= 30) {
                  setTrialDays(value);
                }
              }}
              className="w-full px-3 py-2 border border-border rounded-lg text-sm"
              placeholder="7"
            />
          </div>
          <div className="flex-[2] min-w-[300px]">
            <label className="block text-sm font-medium text-muted-foreground mb-1">Description (Optional)</label>
            <input
              type="text"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              className="w-full px-3 py-2 border border-border rounded-lg text-sm"
              placeholder="e.g., Partner promotion Q1 2025"
            />
          </div>
        </div>
      </div>

      {/* Overview Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-card border border-border rounded-xl p-4">
          <p className="text-muted-foreground text-sm">Total OTP Codes</p>
          <p className="text-2xl font-bold text-foreground">{otps.length}</p>
        </div>
        <div className="bg-card border border-border rounded-xl p-4">
          <p className="text-muted-foreground text-sm">Active Codes</p>
          <p className="text-2xl font-bold text-foreground">{activeOTPs}</p>
        </div>
        <div className="bg-card border border-border rounded-xl p-4">
          <p className="text-muted-foreground text-sm">Total Activations</p>
          <p className="text-2xl font-bold text-foreground">{totalUsages}</p>
        </div>
        <div className="bg-card border border-border rounded-xl p-4">
          <p className="text-muted-foreground text-sm">Avg. Activations</p>
          <p className="text-2xl font-bold text-foreground">
            {otps.length > 0 ? (totalUsages / otps.length).toFixed(1) : '0'}
          </p>
        </div>
      </div>

      {/* Table */}
      <div className="bg-card rounded-xl border border-border shadow-sm overflow-hidden">
        <DataTable
          columns={columns}
          data={otps}
          isLoading={isLoading}
          getRowId={(row) => row.id}
          emptyText="No OTP codes yet."
        />
      </div>

      {/* OTP Created Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-black/40" onClick={() => setShowCreateModal(false)} />
          <div className="relative bg-card rounded-xl border border-border shadow-xl w-full max-w-lg p-6">
            <h2 className="text-lg font-semibold mb-2">OTP Code Created</h2>
            <p className="text-sm text-muted-foreground mb-4">
              Share this reusable OTP code with brands. They can activate their trial using this code on the subscription page.
            </p>
            <div className="flex items-center gap-2 mb-4">
              <input
                readOnly
                value={newOTPCode}
                className="flex-1 px-3 py-2 border border-border rounded-lg bg-muted text-lg font-mono font-bold text-center"
              />
              <button
                onClick={async () => {
                  await navigator.clipboard.writeText(newOTPCode);
                  toast.success('Copied');
                }}
                className="px-3 py-2 rounded-lg border border-border hover:bg-accent text-sm"
              >
                Copy
              </button>
            </div>
            <div className="flex justify-end">
              <button
                onClick={() => setShowCreateModal(false)}
                className="px-4 py-2 rounded-lg bg-[#d7785b] text-white hover:bg-[#c86c51]"
              >
                Done
              </button>
            </div>
          </div>
        </div>
      )}

      {/* OTP Details Modal */}
      {showDetailsModal && selectedOTP && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-black/40" onClick={() => setShowDetailsModal(false)} />
          <div className="relative bg-card rounded-xl border border-border shadow-xl w-full max-w-3xl p-6 max-h-[80vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold">OTP Details: {selectedOTP.code}</h2>
              <button
                onClick={() => setShowDetailsModal(false)}
                className="text-muted-foreground hover:text-muted-foreground"
              >
                ✕
              </button>
            </div>
            <div className="grid grid-cols-2 gap-4 mb-6">
              <div>
                <p className="text-sm text-muted-foreground">Trial Days</p>
                <p className="text-base font-semibold">{selectedOTP.trialDays} days</p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Total Activations</p>
                <p className="text-base font-semibold">{selectedOTP.usageCount}</p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Status</p>
                <p className="text-base font-semibold">{selectedOTP.isActive ? 'Active' : 'Inactive'}</p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Created</p>
                <p className="text-base font-semibold">{new Date(selectedOTP.createdAt).toLocaleDateString()}</p>
              </div>
            </div>

            {selectedOTP.description && (
              <div className="mb-6">
                <p className="text-sm text-muted-foreground mb-1">Description</p>
                <p className="text-base">{selectedOTP.description}</p>
              </div>
            )}

            <h3 className="text-base font-semibold mb-3">Brands Who Used This Code</h3>
            {selectedOTP.usages.length === 0 ? (
              <p className="text-sm text-muted-foreground">No brands have used this code yet.</p>
            ) : (
              <div className="space-y-2">
                {selectedOTP.usages.map((usage: any) => (
                  <div key={usage.id} className="flex items-center justify-between p-3 bg-muted rounded-lg">
                    <div className="flex items-center gap-3">
                      {usage.brand.profileImage ? (
                        <img
                          src={usage.brand.profileImage}
                          alt="brand"
                          className="w-10 h-10 rounded object-cover border"
                        />
                      ) : (
                        <div className="w-10 h-10 rounded-full bg-muted text-muted-foreground flex items-center justify-center border">
                          <span className="text-sm font-semibold">
                            {(usage.brand.companyName || '?').charAt(0).toUpperCase()}
                          </span>
                        </div>
                      )}
                      <div>
                        <p className="font-medium">{usage.brand.companyName || 'Unknown'}</p>
                        <p className="text-xs text-muted-foreground">{usage.brand.shop}</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-xs text-muted-foreground">Activated</p>
                      <p className="text-sm">{new Date(usage.activatedAt).toLocaleString()}</p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
